/** SUNTRONIX ONE — Customer Onboarding Wizard.
 *
 *  10-step draft-and-review flow to onboard a new end customer under a
 *  master client (spec §52).  Every step auto-saves so progress
 *  survives page reloads.  Suntronix internal only.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, ChevronLeft, ChevronRight, Save, Rocket } from "lucide-react";

const STEPS = [
  { key: "identity",   label: "Customer identity" },
  { key: "companies",  label: "Companies" },
  { key: "tech_env",   label: "Technology environment" },
  { key: "deployment", label: "Deployment" },
  { key: "recovery",   label: "Recovery" },
  { key: "warehouse",  label: "Warehouse" },
  { key: "services",   label: "Services" },
  { key: "commercial", label: "Commercial" },
  { key: "contacts",   label: "Contacts" },
  { key: "review",     label: "Review & activate" },
];

const YES_NO = [
  { v: "yes", l: "Yes" }, { v: "no", l: "No" },
  { v: "unknown", l: "Unknown" }, { v: "not_applicable", l: "N/A" },
];

export default function OnboardingWizard() {
  const { draftId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [masterClients, setMasterClients] = useState([]);
  const [mdmOptions, setMdmOptions] = useState([]);
  const [stepKey, setStepKey] = useState(STEPS[0].key);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);

  // Bootstrap: load master clients + existing draft (if any) + master data
  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/master-clients"),
      api.get("/master-data/mdm_uem"),
      draftId ? api.get(`/onboarding/drafts/${draftId}`) : Promise.resolve(null),
    ]).then(([mcs, mdm, d]) => {
      if (cancel) return;
      setMasterClients(mcs.data.items || []);
      setMdmOptions(mdm.data.items || []);
      if (d) {
        setDraft(d.data);
        setStepKey(d.data.current_step || "identity");
      } else {
        // New draft — need to pick a master client first
        const preselectedMc = searchParams.get("master_client_id");
        if (preselectedMc || (mcs.data.items || []).length === 1) {
          const mc_id = preselectedMc || mcs.data.items[0].id;
          api.post("/onboarding/drafts", { master_client_id: mc_id, current_step: "identity", form: {} })
            .then(({ data }) => {
              if (cancel) return;
              setDraft(data);
              navigate(`/customer/new/${data.id}`, { replace: true });
            })
            .catch((e) => toast.error(e?.response?.data?.detail || "Failed to create draft"));
        } else {
          toast.error("Select a master client first");
          navigate("/customers");
        }
      }
    }).catch((e) => toast.error(e?.response?.data?.detail || "Failed to load wizard"));
    return () => { cancel = true; };
  }, [draftId, navigate, searchParams]);

  const currentIdx = STEPS.findIndex((s) => s.key === stepKey);
  const form = useMemo(() => draft?.form || {}, [draft?.form]);

  const patchStep = useCallback((sectionKey, values) => {
    setDraft((d) => ({ ...d, form: { ...d.form, [sectionKey]: { ...(d.form?.[sectionKey] || {}), ...values } } }));
  }, []);

  const saveDraft = useCallback(async (opts = {}) => {
    if (!draft?.id) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/onboarding/drafts/${draft.id}`, {
        current_step: opts.step || stepKey,
        form: draft.form,
      });
      setDraft(data);
      if (opts.silent !== true) toast.success("Draft saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }, [draft, stepKey]);

  const goStep = useCallback(async (nextKey) => {
    await saveDraft({ step: nextKey, silent: true });
    setStepKey(nextKey);
  }, [saveDraft]);

  const activate = useCallback(async () => {
    if (!form?.identity?.name || !form?.identity?.code) {
      toast.error("Customer name and code are required");
      setStepKey("identity"); return;
    }
    setActivating(true);
    try {
      const { data } = await api.post(`/onboarding/drafts/${draft.id}/activate`);
      toast.success(`${form.identity.name} onboarded`);
      navigate(`/customer/${data.master_company_id}/tech-env`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Activation failed"); }
    finally { setActivating(false); }
  }, [draft, form, navigate]);

  if (!draft) {
    return <div className="p-6 text-sm text-[color:var(--st-text-muted)]">Loading onboarding wizard…</div>;
  }
  const mc = masterClients.find((m) => m.id === draft.master_client_id);

  return (
    <div className="max-w-[1200px] mx-auto space-y-6" data-testid="onboarding-wizard">
      <PageHeader
        eyebrow={mc ? `Master client · ${mc.name}` : "Customer Onboarding"}
        title="Onboard a new customer"
        context={<>Step {currentIdx + 1} of {STEPS.length} · {STEPS[currentIdx].label}</>}
        actions={
          <Button variant="outline" onClick={() => saveDraft()} disabled={saving} data-testid="wiz-save">
            <Save className="h-4 w-4 mr-1.5" strokeWidth={1.6} />
            {saving ? "Saving…" : "Save draft"}
          </Button>
        }
        testId="onboarding-header"
      />

      {/* Progress rail */}
      <div className="flex overflow-x-auto gap-1 border-b border-[color:var(--st-border)] -mx-2 px-2" data-testid="wiz-steps">
        {STEPS.map((s, i) => (
          <button key={s.key}
            onClick={() => goStep(s.key)}
            data-testid={`wiz-step-${s.key}`}
            className={
              "px-3 py-2 text-[12px] whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5 " +
              (s.key === stepKey
                ? "border-[color:var(--st-accent)] text-[color:var(--st-text)] font-medium"
                : "border-transparent text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]")
            }>
            <span className={"h-5 w-5 rounded-full grid place-items-center text-[10px] font-mono " +
              (i < currentIdx ? "bg-[color:var(--st-accent)] text-white"
                : i === currentIdx ? "bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)]"
                : "bg-[color:var(--st-surface-alt)] text-[color:var(--st-text-subtle)]")}>
              {i < currentIdx ? <CheckCircle2 className="h-3 w-3" strokeWidth={2} /> : i + 1}
            </span>
            {s.label}
          </button>
        ))}
      </div>

      <div className="st-panel p-6" data-testid={`wiz-panel-${stepKey}`}>
        {stepKey === "identity"   && <StepIdentity   form={form.identity}   set={(v) => patchStep("identity", v)} />}
        {stepKey === "companies"  && <StepCompanies  form={form.companies}  set={(v) => setDraft((d) => ({ ...d, form: { ...d.form, companies: v }}))} />}
        {stepKey === "tech_env"   && <StepTechEnv    form={form}            patch={patchStep} mdmOptions={mdmOptions} />}
        {stepKey === "deployment" && <StepDeployment form={form.deployment} set={(v) => patchStep("deployment", v)} />}
        {stepKey === "recovery"   && <StepRecovery   form={form.recovery}   set={(v) => patchStep("recovery", v)} />}
        {stepKey === "warehouse"  && <StepWarehouse  form={form.warehouse}  set={(v) => patchStep("warehouse", v)} />}
        {stepKey === "services"   && <StepServices   form={form}            set={(v) => setDraft((d) => ({ ...d, form: { ...d.form, services: v.services, lost_stolen_policy: v.lost_stolen_policy, data_trust: v.data_trust } }))} />}
        {stepKey === "commercial" && <StepCommercial form={form.commercial} set={(v) => patchStep("commercial", v)} />}
        {stepKey === "contacts"   && <StepContacts   form={form.contacts}   set={(v) => setDraft((d) => ({ ...d, form: { ...d.form, contacts: v }}))} />}
        {stepKey === "review"     && <StepReview     form={form}            masterClient={mc} />}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="ghost"
          disabled={currentIdx === 0}
          onClick={() => goStep(STEPS[currentIdx - 1].key)}
          data-testid="wiz-back">
          <ChevronLeft className="h-4 w-4 mr-1" strokeWidth={1.6} /> Back
        </Button>
        {currentIdx < STEPS.length - 1 ? (
          <Button onClick={() => goStep(STEPS[currentIdx + 1].key)} data-testid="wiz-next">
            Next <ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.6} />
          </Button>
        ) : (
          <Button onClick={activate} disabled={activating} data-testid="wiz-activate">
            <Rocket className="h-4 w-4 mr-1.5" strokeWidth={1.6} />
            {activating ? "Activating…" : "Activate customer"}
          </Button>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                     Steps                                   */
/* -------------------------------------------------------------------------- */
const Row = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
const Field = ({ label, children }) => (
  <div><Label className="text-[12px]">{label}</Label><div className="mt-1">{children}</div></div>
);
const TxtField = ({ v, on, ...p }) => <Input value={v || ""} onChange={(e) => on(e.target.value)} {...p} />;
const TriField = ({ v, on }) => (
  <Select value={v || ""} onValueChange={on}>
    <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
    <SelectContent>{YES_NO.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
  </Select>
);

function StepIdentity({ form = {}, set }) {
  return (
    <Row>
      <Field label="Customer name *"><TxtField v={form.name} on={(v) => set({ name: v })} placeholder="ABC Global" data-testid="wiz-identity-name" /></Field>
      <Field label="Code *"><TxtField v={form.code} on={(v) => set({ code: v })} placeholder="ABC" data-testid="wiz-identity-code" /></Field>
      <Field label="Country"><TxtField v={form.country} on={(v) => set({ country: v })} placeholder="US" /></Field>
      <Field label="Currency"><TxtField v={form.currency} on={(v) => set({ currency: v })} placeholder="USD" /></Field>
      <Field label="Industry"><TxtField v={form.industry} on={(v) => set({ industry: v })} placeholder="Technology" /></Field>
      <Field label="Website"><TxtField v={form.website} on={(v) => set({ website: v })} placeholder="example.com" /></Field>
    </Row>
  );
}

function StepCompanies({ form = [], set }) {
  const rows = Array.isArray(form) ? form : [];
  const add = () => set([...rows, { name: "", code: "", country: "" }]);
  const upd = (i, patch) => set(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const del = (i) => set(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      <p className="text-[13px] text-[color:var(--st-text-muted)]">Legal entities under this customer.  You can also add these later.</p>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <TxtField v={r.name} on={(v) => upd(i, { name: v })} placeholder="Legal entity name" />
          <TxtField v={r.code} on={(v) => upd(i, { code: v })} placeholder="Code" />
          <TxtField v={r.country} on={(v) => upd(i, { country: v })} placeholder="Country" />
          <Button variant="ghost" onClick={() => del(i)}>Remove</Button>
        </div>
      ))}
      <Button variant="create" onClick={add} data-testid="wiz-add-company">+ Add company</Button>
    </div>
  );
}

function StepTechEnv({ form = {}, patch, mdmOptions }) {
  const apple = form.apple || {}; const windows = form.windows || {};
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-sm font-semibold mb-3">Apple · ABM</h3>
        <Row>
          <Field label="Apple Business Manager"><TriField v={apple.abm_enabled} on={(v) => patch("apple", { abm_enabled: v })} /></Field>
          <Field label="Primary Apple MDM">
            <Select value={apple.primary_mdm || ""} onValueChange={(v) => patch("apple", { primary_mdm: v })}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{mdmOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="AppleCare+"><TriField v={apple.applecare_plus} on={(v) => patch("apple", { applecare_plus: v })} /></Field>
          <Field label="Deployment method"><TxtField v={apple.deployment_method} on={(v) => patch("apple", { deployment_method: v })} placeholder="zero_touch" /></Field>
        </Row>
      </div>
      <div>
        <h3 className="text-sm font-semibold mb-3">Windows · Autopilot</h3>
        <Row>
          <Field label="Autopilot enabled"><TriField v={windows.autopilot_enabled} on={(v) => patch("windows", { autopilot_enabled: v })} /></Field>
          <Field label="Primary management">
            <Select value={windows.primary_mgmt_platform || ""} onValueChange={(v) => patch("windows", { primary_mgmt_platform: v })}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{mdmOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="BitLocker required"><TxtField v={windows.bitlocker} on={(v) => patch("windows", { bitlocker: v })} placeholder="required" /></Field>
          <Field label="Standard build"><TxtField v={windows.standard_build} on={(v) => patch("windows", { standard_build: v })} /></Field>
        </Row>
      </div>
    </div>
  );
}

function StepDeployment({ form = {}, set }) {
  return (
    <Row>
      <Field label="Zero-Touch"><TriField v={form.zero_touch} on={(v) => set({ zero_touch: v })} /></Field>
      <Field label="Asset tag required"><TriField v={form.asset_tag_required ? "yes" : (form.asset_tag_required === false ? "no" : "")} on={(v) => set({ asset_tag_required: v === "yes" })} /></Field>
      <Field label="Employee ack required"><TriField v={form.ack_required ? "yes" : (form.ack_required === false ? "no" : "")} on={(v) => set({ ack_required: v === "yes" })} /></Field>
      <Field label="Packaging standard"><TxtField v={form.packaging_standard} on={(v) => set({ packaging_standard: v })} /></Field>
      <Field label="Shipment method"><TxtField v={form.shipment_method} on={(v) => set({ shipment_method: v })} /></Field>
      <Field label="Delivery evidence"><TxtField v={form.delivery_evidence} on={(v) => set({ delivery_evidence: v })} /></Field>
    </Row>
  );
}
function StepRecovery({ form = {}, set }) {
  return (
    <Row>
      <Field label="Recovery enabled"><TriField v={form.enabled} on={(v) => set({ enabled: v })} /></Field>
      <Field label="Home pickup"><TriField v={form.home_pickup ? "yes" : (form.home_pickup === false ? "no" : "")} on={(v) => set({ home_pickup: v === "yes" })} /></Field>
      <Field label="Office pickup"><TriField v={form.office_pickup ? "yes" : (form.office_pickup === false ? "no" : "")} on={(v) => set({ office_pickup: v === "yes" })} /></Field>
      <Field label="Pickup attempts"><TxtField v={form.pickup_attempts} on={(v) => set({ pickup_attempts: v })} placeholder="3" /></Field>
      <Field label="SLA"><TxtField v={form.sla} on={(v) => set({ sla: v })} placeholder="72 hours" /></Field>
    </Row>
  );
}
function StepWarehouse({ form = {}, set }) {
  return (
    <Row>
      <Field label="Suntronix warehousing"><TriField v={form.suntronix_warehousing} on={(v) => set({ suntronix_warehousing: v })} /></Field>
      <Field label="Customer stock held"><TriField v={form.customer_stock_held} on={(v) => set({ customer_stock_held: v })} /></Field>
      <Field label="Free storage days"><TxtField v={form.free_storage_days} on={(v) => set({ free_storage_days: v })} placeholder="90" /></Field>
      <Field label="Storage billing"><TxtField v={form.storage_billing} on={(v) => set({ storage_billing: v })} placeholder="included" /></Field>
    </Row>
  );
}
function StepServices({ form = {}, set }) {
  const SERVICES = ["Procurement","Warehousing","Deployment","Recovery","Repair","Warranty","Loaner","Buyback","ITAD","Managed ITAM"];
  const list = Array.isArray(form.services) ? form.services :
                 (Array.isArray(form) ? form : []);
  const policy = form.lost_stolen_policy || {};
  const trust = form.data_trust || {};
  const engaged = new Set(list);
  const toggle = (s) => {
    engaged.has(s) ? engaged.delete(s) : engaged.add(s);
    set({ services: Array.from(engaged),
            lost_stolen_policy: policy,
            data_trust: trust });
  };
  const setPolicy = (patch) =>
    set({ services: Array.from(engaged),
            lost_stolen_policy: { ...policy, ...patch },
            data_trust: trust });
  const setTrust = (patch) =>
    set({ services: Array.from(engaged),
            lost_stolen_policy: policy,
            data_trust: { ...trust, ...patch } });
  return (
    <div className="space-y-4">
      <p className="text-[13px] text-[color:var(--st-text-muted)]">Which Suntronix services are engaged for this customer?</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {SERVICES.map((s) => (
          <label key={s} className="flex items-center gap-2 p-3 st-panel rounded-md cursor-pointer">
            <input type="checkbox" checked={engaged.has(s)} onChange={() => toggle(s)} />
            <span className="text-sm">{s}</span>
          </label>
        ))}
      </div>

      <div className="st-panel p-3 space-y-2" data-testid="wiz-lost-stolen">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">
          Lost / Stolen policy
        </div>
        <div className="text-[12px] text-slate-500">
          Directives that gate the remote-lock / wipe / police / insurance flow when a device is reported.
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[13px]">
          {[
            ["remote_lock_required",   "Remote lock",      true],
            ["remote_wipe_required",   "Remote wipe",      true],
            ["police_report_required", "Police report",    false],
            ["insurance_claim_required","Insurance claim", false],
          ].map(([key, label, dflt]) => (
            <label key={key} className="flex items-center gap-2">
              <input type="checkbox"
                checked={policy[key] !== undefined ? !!policy[key] : dflt}
                onChange={(e) => setPolicy({ [key]: e.target.checked })}
                data-testid={`wiz-ls-${key}`} />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Field label="Notify within (hours)">
            <TxtField type="number" v={policy.notify_within_hours ?? 24}
              on={(v) => setPolicy({ notify_within_hours: Number(v) || 24 })}
              placeholder="24" />
          </Field>
          <Field label="Escalation email">
            <TxtField v={policy.escalation_email}
              on={(v) => setPolicy({ escalation_email: v })}
              placeholder="security@customer.com" />
          </Field>
        </div>
      </div>

      <div className="st-panel p-3 space-y-2" data-testid="wiz-data-trust">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">
          Data Trust intent
        </div>
        <div className="text-[12px] text-slate-500">
          These flags feed the weekly Data Trust digest.  The formal retention, SoT and logistics setup happens after activation from the Customer 360 &gt; Data Trust tab.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Field label="DPA signed"><TriField
            v={trust.dpa_signed ? "yes" : (trust.dpa_signed === false ? "no" : "")}
            on={(v) => setTrust({ dpa_signed: v === "yes" })} /></Field>
          <Field label="Privacy contact email">
            <TxtField v={trust.privacy_contact_email}
              on={(v) => setTrust({ privacy_contact_email: v })}
              placeholder="privacy@customer.com" />
          </Field>
          <Field label="Data residency (comma-sep ISO2)">
            <TxtField v={(trust.data_residency || []).join(", ")}
              on={(v) => setTrust({ data_residency: v.split(",").map((c) => c.trim().toUpperCase()).filter(Boolean) })}
              placeholder="IN, SG, GB" />
          </Field>
          <Field label="Cross-border expected"><TriField
            v={trust.cross_border ? "yes" : (trust.cross_border === false ? "no" : "")}
            on={(v) => setTrust({ cross_border: v === "yes" })} /></Field>
        </div>
      </div>
    </div>
  );
}
function StepCommercial({ form = {}, set }) {
  const contracts = Array.isArray(form.contracts) ? form.contracts : [];
  const setContracts = (rows) => set({ contracts: rows });
  const addContract = () => setContracts([...contracts,
    { contract_number: "", contract_type: "MSA",
      start_date: "", end_date: "", active: true }]);
  const updateContract = (i, patch) => setContracts(
    contracts.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const removeContract = (i) => setContracts(
    contracts.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      <Row>
        <Field label="Service package"><TxtField v={form.service_package} on={(v) => set({ service_package: v })} /></Field>
        <Field label="Billing entity"><TxtField v={form.billing_entity} on={(v) => set({ billing_entity: v })} /></Field>
        <Field label="Currency"><TxtField v={form.currency} on={(v) => set({ currency: v })} placeholder="USD" /></Field>
        <Field label="PO required"><TriField v={form.po_required ? "yes" : (form.po_required === false ? "no" : "")} on={(v) => set({ po_required: v === "yes" })} /></Field>
        <Field label="Invoice frequency"><TxtField v={form.invoice_frequency} on={(v) => set({ invoice_frequency: v })} placeholder="monthly" /></Field>
      </Row>

      <div className="st-panel p-3 space-y-2" data-testid="wiz-contracts">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Contracts on file</div>
            <div className="text-[12px] text-slate-500">MSAs and SoWs — new service requests are refused when all configured contracts expire.</div>
          </div>
          <Button variant="create" size="sm" onClick={addContract}
            data-testid="wiz-contracts-add">+ Add contract</Button>
        </div>
        {contracts.length === 0 && (
          <div className="text-[12px] text-slate-400 py-3 text-center">
            No contracts captured in the wizard yet — you can add them here or later from Customer 360.
          </div>
        )}
        {contracts.map((c, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end border-t border-slate-100 pt-2"
            data-testid={`wiz-contract-row-${i}`}>
            <TxtField v={c.contract_number} on={(v) => updateContract(i, { contract_number: v })}
              placeholder="Contract number" />
            <Select value={c.contract_type || "MSA"}
              onValueChange={(v) => updateContract(i, { contract_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["MSA","SoW","Addendum","NDA","Other"].map((t) =>
                  <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <TxtField type="date" v={c.start_date} on={(v) => updateContract(i, { start_date: v })} />
            <TxtField type="date" v={c.end_date} on={(v) => updateContract(i, { end_date: v })} />
            <Button variant="ghost" size="sm" onClick={() => removeContract(i)}>Remove</Button>
          </div>
        ))}
      </div>
    </div>
  );
}
function StepContacts({ form = [], set }) {
  const rows = Array.isArray(form) ? form : [];
  const ROLES = ["IT Admin","Procurement","Finance","HR","Apple/MDM Admin","Security","Warehouse","Approver","Escalation","Management"];
  const add = () => set([...rows, { role: "IT Admin", name: "", email: "", phone: "" }]);
  const upd = (i, patch) => set(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const del = (i) => set(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      <p className="text-[13px] text-[color:var(--st-text-muted)]">Key operational contacts for this customer.</p>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
          <Select value={r.role} onValueChange={(v) => upd(i, { role: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <TxtField v={r.name} on={(v) => upd(i, { name: v })} placeholder="Name" />
          <TxtField v={r.email} on={(v) => upd(i, { email: v })} placeholder="Email" />
          <TxtField v={r.phone} on={(v) => upd(i, { phone: v })} placeholder="Phone" />
          <Button variant="ghost" onClick={() => del(i)}>Remove</Button>
        </div>
      ))}
      <Button variant="create" onClick={add} data-testid="wiz-add-contact">+ Add contact</Button>
    </div>
  );
}
function StepReview({ form, masterClient }) {
  const sections = [
    { label: "Identity", data: form.identity },
    { label: "Companies", data: (form.companies || []).length + " company(ies)" },
    { label: "Apple / ABM", data: form.apple },
    { label: "Windows / Autopilot", data: form.windows },
    { label: "Deployment", data: form.deployment },
    { label: "Recovery", data: form.recovery },
    { label: "Warehouse", data: form.warehouse },
    { label: "Services", data: form.services },
    { label: "Commercial", data: form.commercial },
    { label: "Contacts", data: (form.contacts || []).length + " contact(s)" },
  ];
  return (
    <div className="space-y-4">
      <div className="st-panel p-4">
        <div className="text-sm font-semibold mb-2">Ready to activate</div>
        <div className="text-[13px] text-[color:var(--st-text-muted)]">
          <b>{form?.identity?.name || "—"}</b> will be created under <b>{masterClient?.name || "—"}</b> with the configuration below.
          Activation creates the customer + technology environment.  All fields remain editable afterwards.
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sections.map((s) => (
          <div key={s.label} className="st-panel p-3">
            <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">{s.label}</div>
            <div className="text-[13px] mt-1 whitespace-pre-wrap break-words font-mono">
              {typeof s.data === "string" ? s.data : JSON.stringify(s.data || {}, null, 2).slice(0, 400)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
